import { css } from "@emotion/react";
import { EnrichedSwarovskiProductFrontend, type TranslatedValue } from "@swo/storefront";
import cn from "classnames";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ContentContainer } from "../../components/_shared-components/arrangement/Container";
import { Spacer } from "../../components/_shared-components/arrangement/Space";
import { ProgressBar } from "../../components/_shared-components/ui-elements/SwarovskiGenericCarouselComponent/subcomponents/ProgressBar";
import { usePageContext } from "../../global/page-context";
import { useOnResize } from "../../hooks/use-on-resize";
import { CSS_MQ } from "../../styles/breakpoints";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { nonEmpty } from "../../utils/fp/small-fp-utils";
import { CarouselProductView } from "./CarouselProductView";
import { mapProduct, type PDPCarouselProduct } from "./mapProduct";
import { PDPHeadlineTypography } from "./pdp-headline-typography";
import { T } from "../../components/T";

export type Product = Pick<EnrichedSwarovskiProductFrontend, "key" | "path" | "name" | "images" | "startingPrice">;

export const OtherProductsInFamily: ReactTFC<{
  headline: TranslatedValue | undefined;
  products: NonEmptyArray<Product>;
  selectedProductKey: string | undefined;
  centeredIfNotFilled: boolean;
}> = props => {
  const { generalTranslations, pageBase } = usePageContext();
  const fromPriceLabel = generalTranslations("fromPriceLabel");
  const { locale } = pageBase;

  const containerRef = useRef<HTMLDivElement | null>(null);

  type State = {
    progress: number;
    elementGrossWidth: number;
    elementOffset: (idx: number) => number;
  };

  const [state, setState] = useState<State>({ progress: 0, elementGrossWidth: 170, elementOffset: (idx: number) => 0 });
  const productMapper = mapProduct(props.selectedProductKey, fromPriceLabel, locale);

  const products: Array<PDPCarouselProduct> = props.products.map(productMapper).filter(nonEmpty);

  const [ready, setReady] = useState(false);
  const selectedIdx = products.findIndex(p => p.isSelected);

  const [initialized, setInitialized] = useState(selectedIdx < 0);

  const ref = useRef<HTMLDivElement | null>(null);

  const productsCount = props.products.length;
  const visiblePerPage = containerRef.current ? containerRef.current.clientWidth / state.elementGrossWidth : 0;

  const calculate = useCallback(() => {
    const { current } = ref;

    if (!current) {
      return;
    }

    const { scrollWidth, clientWidth, scrollLeft } = current;

    const progress = scrollLeft / (scrollWidth - clientWidth);
    const elementGrossWidth = scrollWidth / productsCount;

    const gapSize = Number.parseFloat(getComputedStyle(current).getPropertyValue("--gap"));
    const elementNettWidth = (scrollWidth - (productsCount - 1) * gapSize) / productsCount;

    const elementOffset = (idx: number) => {
      const raw = idx * (elementNettWidth + gapSize);

      // Move the item by the offset
      // so that it's not covered with the overflow shadow (from the left)
      return Math.max(0, raw - 40);
    };

    setState({ progress, elementGrossWidth, elementOffset });
    setReady(true);
  }, []);

  if (ready && !initialized) {
    if (selectedIdx >= 0) {
      const pageIdx = Math.floor(selectedIdx / Math.max(1, Math.floor(visiblePerPage)));

      seekToIndex({ pageIdx });
      setInitialized(true);
    }
  }

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
      calculate();
    },
    [calculate],
  );

  useEffect(() => {
    calculate();
  }, [calculate]);

  useEffect(() => {
    calculate();
  }, []);

  useOnResize(calculate);

  if (products.length === 0 || products.every(p => p.isSelected)) {
    return null;
  }

  function getOverflows() {
    const { current } = ref;

    if (!current) {
      return null;
    }

    const hasOverflowLeft = current.scrollLeft > 0;
    const hasOverflowRight = current.scrollWidth > current.clientWidth + current.scrollLeft;

    return {
      hasOverflowLeft,
      hasOverflowRight,
    };
  }

  function seekToIndex(progress: { pageIdx: number }) {
    const { current } = ref;

    if (!current) {
      return;
    }

    const scrollToElementIdx = progress.pageIdx * Math.floor(visiblePerPage);
    current.scrollLeft = state.elementOffset(scrollToElementIdx);
  }

  const overflows = getOverflows();

  return (
    <>
      <Spacer size={"md"} />

      <ContentContainer className={"js-other-products-from-family"}>
        {props.headline && (
          <span css={headlineStyles}>
            <T>{props.headline}</T>
          </span>
        )}

        <div
          className={cn({
            "overflow-left": overflows?.hasOverflowLeft,
            "overflow-right": overflows?.hasOverflowRight,
          })}
          ref={containerRef}
          css={containerStyles}
        >
          <div
            onScroll={onScroll}
            ref={ref}
            className={cn("u-hide-scrollbar-x", {
              centered:
                props.centeredIfNotFilled && overflows && !overflows.hasOverflowLeft && !overflows.hasOverflowRight,
            })}
            css={carouselWrapperStyles}
          >
            {products.map(product => (
              <CarouselProductView key={product.key} product={product} />
            ))}
          </div>
        </div>

        <ProgressBar
          scrollProgress={state.progress}
          visiblePerPage={visiblePerPage}
          onSeekRequested={seekToIndex}
          imagesCount={products.length}
        />
      </ContentContainer>
    </>
  );
};

const containerStyles = css`
  position: relative;

  &.overflow-left {
    &::before {
      content: "";
      z-index: 10;

      position: absolute;
      width: 50px;
      height: calc(100% - 12px);
      top: 0;
      left: -1px; // Safari iOS <3

      background: linear-gradient(90deg, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 20%, rgba(0, 0, 0, 0));
    }
  }

  &.overflow-right {
    &::after {
      content: "";
      z-index: 10;

      position: absolute;
      width: 50px;
      height: calc(100% - 12px);
      top: 0;
      right: -1px; // Safari iOS <3

      background: linear-gradient(270deg, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 20%, rgba(0, 0, 0, 0));
    }
  }
`;

const headlineStyles = [
  PDPHeadlineTypography,
  css`
    margin-bottom: 20px;
    display: block;
  `,
];

const carouselWrapperStyles = [
  css`
    --h: 5px;
    --bg: ${CSS_COLORS.Grey0};

    --gap: 12px;
    ${CSS_MQ.atLeastMd} {
      --gap: 20px;
    }

    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 2px 0 12px;

    &.centered {
      justify-content: center;
    }

    gap: var(--gap);

    scroll-behavior: smooth;
  `,
];
